<template>
  <div
    class="gmb-more-hours-mass-edit"
    :class="{ disabled: !canEditFacilityHours }"
  >
    <v-row class="max-w-560">
      <v-col class="d-flex align-center pb-0 max-w-50px">
        <v-checkbox
          class="mt-0"
          v-model="checkbox"
          @change="onCheckboxClicked"
          :disabled="!canEditFacilityHours"
        ></v-checkbox>
      </v-col>
      <v-col class="d-flex align-center pb-0">
        <paragraph class="border-accent">
          {{ $t("message.facilityDetails.moreHours.facilitiesMoreHours") }}
        </paragraph>
      </v-col>
    </v-row>
    <v-row v-if="!moreHours.length" class="position-relative">
      <v-col class="max-w-50px" />
      <v-col>
        <div class="description">
          {{ $t("message.facilityDetails.moreHours.noMoreHoursSelected") }}
        </div>
        <InfoBox
          class="mt-2"
          :text="$t('message.facilityDetails.moreHours.editMessage')"
        />
        <div class="actions" v-if="checkbox">
          <div class="action" v-for="action in actions" :key="action.type">
            <div class="description muted">
              {{ $t(action.msg) }}
            </div>
            <v-btn v-bind="action.btnProps" @click="handleAction(action.type)">
              {{ $t(action.btnLabel) }}
            </v-btn>
            <v-tooltip right :max-width="tooltipMaxWidth">
              <template v-slot:activator="{ on }">
                <div class="info-icon" v-on="on">
                  <v-icon color="lime">mdi-information</v-icon>
                </div>
              </template>
              {{ $t(action.desc) }}
            </v-tooltip>
          </div>
        </div>
      </v-col>
    </v-row>
    <template v-if="moreHours.length">
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <div class="active-action">
            <v-row>
              <v-col cols="11" class="d-flex align-center">
                <span class="section-title">{{ activeActionLabel }}:</span>
              </v-col>
              <v-col cols="1">
                <v-btn
                  icon
                  color="primary"
                  @click="handleAction(selectedActionType)"
                >
                  <v-icon>
                    icon-edit
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <ul
            v-if="
              selectedActionType === actionTypes.SET && moreHours.length > 0
            "
            class="gmb-more-hours-mass-edit__preview-list"
          >
            <MoreHoursCategoryListItem
              class="gmb-more-hours-mass-edit__preview-list-item"
              v-for="item in moreHours"
              :key="item.id"
              :item="item"
            />
          </ul>
          <ul
            v-if="
              selectedActionType === actionTypes.DELETE && moreHours.length > 0
            "
            class="gmb-more-hours-mass-edit__preview-list"
          >
            <li
              v-for="category in moreHours"
              :key="category.type"
              class="gmb-more-hours-mass-edit__preview-list-item--delete"
            >
              {{ category.name }}
            </li>
          </ul>

          <div class="change-action" v-if="selectedActionType">
            <div class="description muted mb-0">
              {{ $t("message.facilityDetails.moreHours.changeType") }}
            </div>
            <v-btn
              v-for="action in notActiveActions"
              :key="action.type"
              text
              color="primary"
              @click="changeAction(action.type)"
              class="ml-4"
            >
              {{ $t(action.btnLabel) }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <MoreHoursCategoriesDialog
      :is-open="dialog.categories"
      :temp-values="tempValues"
      :facilities-ids="facilitiesIds"
      :delete-mode="deleteMode"
      @setTempValues="tempValues = $event"
      @close="closeCategoriesDialog"
      @edit="handleEditCategory"
      @saveDelete="handleSaveDelete"
      @save="handleSave"
    />

    <OpeningHoursDialog
      v-if="dialog.openingHours"
      :maxWidth="1154"
      :days="chosenCategory.values"
      :item="chosenCategory.info"
      :is-more-hours-version="true"
      @save="handleSaveCategoryHours"
      @close="closeOpeningHoursDialog"
    />
  </div>
</template>

<script>
import MoreHoursCategoryListItem from "@/components/FacilityDetails/GmbMoreHours/MoreHoursCategoryListItem.vue";
import MoreHoursCategoriesDialog from "@/components/FacilityDetails/GmbMoreHours/MoreHoursCategoriesDialog.vue";
import OpeningHoursDialog from "@/components/FacilityDetails/Dialog/OpeningHoursDialog.vue";
import Paragraph from "@/components/ui/Paragraph";
import InfoBox from "@/components/ui/InfoBox.vue";

import cuid from "cuid";
import { DialogHelper } from "@/helpers/dialog-helper";
import { GmbActionType } from "@/enums/gmb-attribute-action-type";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";

export default {
  name: "MoreHoursMassEdit",
  components: {
    MoreHoursCategoryListItem,
    MoreHoursCategoriesDialog,
    OpeningHoursDialog,
    Paragraph,
    InfoBox
  },
  mixins: [permissionsInfo],
  data() {
    return {
      dialog: {
        categories: false,
        openingHours: false
      },
      checkbox: false,
      moreHours: [],
      tooltipMaxWidth: 436,
      actions: [],
      facilitiesIds: [],
      actionTypes: GmbActionType,
      selectedAction: null,
      selectedActionType: null,
      tempValues: [],
      chosenCategory: null
    };
  },
  computed: {
    activeActionLabel() {
      const labelKey = "message.facilityDetails.moreHours.";
      if (this.selectedActionType === this.actionTypes.SET) {
        return this.$t(labelKey + "settingMoreHours");
      } else if (this.selectedActionType === this.actionTypes.DELETE) {
        return this.$t(labelKey + "deletingMoreHours");
      } else {
        return "";
      }
    },
    notActiveActions() {
      return this.actions.filter(
        ({ type }) => type !== this.selectedActionType
      );
    },
    deleteMode() {
      return (
        this.selectedAction && this.selectedAction.type === GmbActionType.DELETE
      );
    }
  },
  mounted() {
    this.facilitiesIds = this.$route.params.ids.split(",");
  },
  created() {
    this.setActions();
  },
  methods: {
    // MASS EDIT LOGIC
    setActions() {
      this.actions = [
        {
          type: this.actionTypes.SET,
          msg: "message.facilityDetails.moreHours.setMoreHoursQuestion",
          desc: "message.facilityDetails.moreHours.setMoreHoursDescription",
          btnLabel: "message.facilityDetails.moreHours.setMoreHours",
          btnProps: {
            color: "primary"
          }
        },
        {
          type: this.actionTypes.DELETE,
          msg: "message.facilityDetails.moreHours.deleteMoreHoursQuestion",
          desc: "message.facilityDetails.moreHours.deleteMoreHoursDescription",
          btnLabel: "message.facilityDetails.moreHours.deleteMoreHours",
          btnProps: {
            color: "primary"
          }
        }
      ];
    },
    async onCheckboxClicked(newVal) {
      this.$emit("update:checked", newVal);
      if (!newVal) {
        this.moreHours = [];
        this.tempValues = [];
        this.selectedAction = null;
        this.selectedActionType = null;
        this.$emit("update:moreHours", this.moreHours);
      }
    },
    changeAction(actionType) {
      DialogHelper.showConfirmDialog(
        {
          text: this.$t("message.facilityDetails.moreHours.changeTypeConfirm"),
          okLabelKey: "message.common.continue"
        },
        () => {
          this.handleAction(actionType);
        }
      );
    },
    handleAction(actionType) {
      if (this.selectedAction && this.selectedAction.type !== actionType) {
        this.moreHours = [];
        this.tempValues = [];
      }

      this.selectedAction = this.actions.find(
        action => action.type === actionType
      );

      this.dialog.categories = true;
    },

    handleSaveDelete(moreHours) {
      this.tempValues = [...moreHours];
      this.moreHours = [...moreHours];
      this.selectedActionType = this.selectedAction.type;
      this.$emit("update:type", this.selectedActionType);
      this.$emit("update:moreHours", this.moreHours);
      this.closeCategoriesDialog();
    },

    handleSave(moreHours) {
      this.moreHours = [...moreHours];
      this.selectedActionType = this.selectedAction.type;
      this.$emit("update:type", this.selectedActionType);
      this.$emit("update:moreHours", this.moreHours);
      this.closeCategoriesDialog();
    },

    // MORE HOURS LOGIC
    closeCategoriesDialog() {
      this.dialog.categories = false;
    },
    async closeOpeningHoursDialog() {
      this.dialog.openingHours = false;

      await this.$nextTick(() => {
        this.dialog.categories = true;
      });

      await this.$nextTick(() => {
        this.chosenCategory = null;
      });
    },
    generateCategoryValues(values) {
      const generatedValues = {};

      const days = values || {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
      };

      for (const [key, value] of Object.entries(days)) {
        generatedValues[key] = {
          name: this.$t(`message.common.${key}`),
          id: cuid(),
          value
        };
      }

      return generatedValues;
    },

    handleEditCategory(category) {
      this.chosenCategory = {
        id: category.id || cuid(),
        info: { title: category.name, type: category.type },
        values: this.generateCategoryValues(category.values)
      };

      this.dialog.categories = false;
      this.dialog.openingHours = true;
    },

    handleSaveCategoryHours(hours) {
      const foundIndex = this.tempValues.findIndex(
        value => value.id === this.chosenCategory.id
      );

      if (foundIndex > -1) {
        Object.entries(hours).forEach(([k, v]) => {
          this.tempValues[foundIndex].values[k] = v.value;
        });
      } else {
        const formattedValues = {};
        Object.entries(hours).forEach(([k, v]) => {
          formattedValues[k] = v.value;
        });

        this.tempValues.push({
          id: cuid(),
          name: this.chosenCategory.info.title,
          type: this.chosenCategory.info.type,
          values: formattedValues
        });
      }
      this.closeOpeningHoursDialog();
    },

    setTempValues(values) {
      this.tempValues = values;
    }
  }
};
</script>

<style lang="scss" scoped>
.gmb-more-hours-mass-edit {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding: $spacing * 1.5 0;

  &__preview-list {
    padding-left: 0;
    max-width: 560px;
  }

  &__preview-list-item {
    &:not(:first-child) {
      margin-top: $spacing;
    }

    &--delete {
      list-style: none;
      color: $primary;
    }
  }
}

.section-title {
  @include font-size(16px);
  line-height: 24px;
  font-weight: 600;
  color: $grey-darker;
}

.description {
  @include font-size(14px);
  line-height: 17px;
  color: $grey-darker;
  margin-bottom: $spacing;

  &.muted {
    line-height: 19px;
    opacity: 0.7;
  }
}

.actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-evenly;
  margin-right: -$spacing * 5.75;

  .action {
    flex-basis: 170px;
    position: relative;
    margin-top: $spacing * 5;
    margin-right: $spacing * 5.75;

    .description {
      text-align: center;
      margin-bottom: $spacing * 2.5;
    }

    .info-icon {
      display: inline;
      position: absolute;
      top: 6px;
      right: -30px;
    }
  }

  .v-btn {
    width: 100%;
  }
}

.active-action {
  border-bottom: 1px solid $light-3;
}

.change-action {
  display: flex;
  align-items: center;
  margin-top: $spacing * 2.5;
  padding-top: $spacing;
  border-top: 1px solid $light-3;
}

.max-w-560 {
  max-width: 560px;
}

.border-accent {
  @include borderAccent($lime);
}

.v-btn {
  text-transform: none;
}
</style>
